const Keys = {
  A: "a",
  C: "c",
  I: "i",
  J: "j",
  P: "p",
  S: "s",
  U: "u",
  X: "x",
  SHIFT: "shift",
  CTRL: "control",
  ALT: "alt",
  F12: "f12",
  PRINT: "printscreen",
};

const KeyCodes = {
  METALEFT: "MetaLeft",
  CONTEXT: "ContextMenu",
  OSLEFT: "OSLeft",
  F12: "F12",
};

const BrowserPrefixes = {
  WEBKIT: "webkit",
  MOZ: "moz",
  MS: "ms",
};

const isKeyPressed = (eventKey: string, key: string): boolean => {
  return eventKey?.toLowerCase() === key;
};

// Prevents user from selecting text. Elements with class "copy-item" are exempt.
const disableSelection = (targetElement: HTMLElement) => {
  if (typeof targetElement.onselectstart !== "undefined") {
    targetElement.onselectstart = (event) => {
      if ((event.target as HTMLElement)?.parentElement?.closest(".copy-item")) {
        return true;
      }
      return false;
    };
  } else {
    for (const browserPrefix in BrowserPrefixes) {
      targetElement.style.setProperty(
        `-${BrowserPrefixes[browserPrefix as keyof typeof BrowserPrefixes]}-user-select`,
        "none"
      );
    }
  }
};

// Prevents user from accessing the context menu (right-click), except on inputs/passwords
const disableContextMenu = (e: Event): boolean => {
  const targetElement = e.target as HTMLInputElement;
  if (
    (targetElement.nodeName === "A" && targetElement.type === "text") ||
    targetElement.type === "password"
  ) {
    return true;
  }
  return false;
};

/** @internal – exported only for test spying (jsdom 26 locks window.location.reload) */
export const _reload = () => window.location.reload();

// Prevents print screen
const disablePrint = (e: KeyboardEvent) => {
  if (isKeyPressed(e.key, Keys.PRINT)) {
    _reload();
  }
};

// Disables keyboard shortcuts: Ctrl+C, Ctrl+A, Ctrl+S, Ctrl+U, Ctrl+P, Ctrl+X,
// Ctrl+Shift+I/J (DevTools), F12, Meta/OS key
const disableKeyboardActions = (e: KeyboardEvent) => {
  disablePrint(e);
  if (isKeyPressed(e.key, Keys.F12)) {
    e.preventDefault();
    return;
  }
  const disabledKeys = [
    Keys.A,
    Keys.C,
    Keys.P,
    Keys.S,
    Keys.U,
    Keys.X,
    Keys.SHIFT,
    Keys.I,
    Keys.J,
    Keys.CTRL,
    Keys.ALT,
    Keys.F12,
  ];
  const disabledKeyCodes = [KeyCodes.METALEFT, KeyCodes.OSLEFT, KeyCodes.CONTEXT, KeyCodes.F12];
  const actionIncludesKeys =
    disabledKeys.includes(e?.key?.toLowerCase()) || disabledKeyCodes.includes(e.code);
  if ((e.ctrlKey || e.metaKey) && actionIncludesKeys) {
    e.preventDefault();
  }
};

/**
 * Registers all browser protection actions:
 * - Disables text selection on body (exempts .copy-item elements)
 * - Disables drag
 * - Disables context menu (right-click)
 * - Disables keyboard shortcuts (Ctrl+C, Ctrl+A, F12, etc.)
 *
 * Call this once on the client side (e.g. inside useEffect).
 * Returns a cleanup function to remove all listeners.
 */
export const registerBrowserActions = (): (() => void) => {
  const onLoad = () => disableSelection(document.body);
  window.addEventListener("load", onLoad);
  document.ondragstart = () => false;
  document.oncontextmenu = disableContextMenu;

  const keyboardHandler = (e: Event) => disableKeyboardActions(e as KeyboardEvent);
  window.addEventListener("keyup", keyboardHandler, false);
  window.addEventListener("keydown", keyboardHandler, false);

  return () => {
    window.removeEventListener("load", onLoad);
    window.removeEventListener("keyup", keyboardHandler, false);
    window.removeEventListener("keydown", keyboardHandler, false);
    document.ondragstart = null;
    document.oncontextmenu = null;
  };
};
